<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap gap-2">
      <ShopListingFilterEntityFilter
        v-if="manufacturerFilter"
        v-model="manufacturer"
        :filter="manufacturerFilter"
      />
      <ShopListingFilterPropertyFilter
        v-for="filter in propertyFilters"
        :key="filter.name"
        v-model="properties[filter.name]"
        :filter="filter as PropertyFilter"
      />
    </div>
    <div class="mt-2 flex flex-wrap gap-1">
      <button
        v-if="manufacturer"
        class="rounded-full bg-black px-4 py-2 text-xs text-white"
        @click="manufacturer = undefined"
      >
        <span class="flex items-center gap-3">
          {{ $t('entity.manufacturer.label') }}: {{ manufacturerLabel }}
          <IconX />
        </span>
      </button>
      <UiPill
        v-for="activeProperty in activeProperties"
        :key="activeProperty.id"
        :label="activeProperty.group"
        :value="activeProperty.value"
        @click="removePropertyFilter(activeProperty)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getListingFilters, getTranslatedProperty } from '@shopware/helpers'
import type { Schemas } from '#shopware'
import type { Filter as PropertyFilter } from '~/components/shop/listing/filter/PropertyFilter.vue'
import type { Filter as EntityFilter } from '~/components/shop/listing/filter/EntityFilter.vue'

const props = defineProps<{
  availableFilters: ReturnType<typeof getListingFilters>
}>()

const filters = computed(
  () => props.availableFilters as unknown as (PropertyFilter | EntityFilter)[]
)

const manufacturerFilter = computed(
  () =>
    filters.value.find((filter) => filter.code === 'manufacturer') as
      | EntityFilter
      | undefined
)

const propertyFilters = computed(
  () =>
    filters.value.filter(
      (filter) => filter.code === 'properties'
    ) as PropertyFilter[]
)

const criteria = defineModel<Schemas['ProductListingCriteria']>()

const manufacturer = ref<string | undefined>(
  criteria.value?.manufacturer || undefined
)

const manufacturerLabel = computed(() => {
  if (!manufacturer.value) {
    return undefined
  }
  return getTranslatedProperty(
    manufacturerFilter.value?.entities.find((e) => e.id === manufacturer.value),
    'name'
  )
})

const properties = ref(
  propertyFilters.value.reduce(
    (acc, filter) => {
      const ids = criteria.value?.properties
        ?.split('|')
        .filter((id) => filter.options.some((o) => o.id === id))
      acc[filter.name] = ids || []
      return acc
    },
    {} as Record<string, string[]>
  )
)

const activeProperties = computed(() => {
  const active = Object.values(properties.value)
    .flat()
    .map((id) => {
      const filter = propertyFilters.value.find((f) =>
        f.options.some((o) => o.id === id)
      )
      const value = filter?.options.find((o) => o.id === id)
      return value
        ? {
            id,
            group: getTranslatedProperty(filter, 'label'),
            value: getTranslatedProperty(value, 'name'),
          }
        : undefined
    })
  return active.filter((v) => !!v) as {
    id: string
    group: string
    value: string
  }[]
})

const removePropertyFilter = (filter: { id: string }) => {
  properties.value = Object.entries(properties.value).reduce(
    (acc, [key, value]) => {
      acc[key] = value.filter((id) => id !== filter.id)
      return acc
    },
    {} as Record<string, string[]>
  )
}

watch(manufacturer, (value) => {
  criteria.value = {
    ...criteria.value,
    manufacturer: value,
  }
})

watchDeep(properties, (value) => {
  criteria.value = {
    ...criteria.value,
    properties: Object.values(value)
      .map((ids) => ids.join('|'))
      .join('|'),
  }
})
</script>
